import { useEffect, useRef, useCallback, useState } from 'react';

export const AUTO_SIGNOUT_KEY = 'auto_signout_timeout';

/** Maps the setting value to total session timeout in ms (including grace period). */
const TIMEOUT_MS: Record<string, number> = {
  '15min':  15 * 60 * 1000,
  '30min':  30 * 60 * 1000,
  '1hour':  60 * 60 * 1000,
  '2hours': 120 * 60 * 1000,
  '4hours': 240 * 60 * 1000,
};

/** Warning countdown displayed before automatic sign-out (seconds). */
export const GRACE_SECONDS = 120;
const GRACE_MS = GRACE_SECONDS * 1000;

/** Returns the idle-before-warning delay in ms, or null if disabled. */
function getIdleMs(): number | null {
  try {
    const s = localStorage.getItem(AUTO_SIGNOUT_KEY);
    if (!s || s === 'disabled' || !TIMEOUT_MS[s]) return null;
    return Math.max(TIMEOUT_MS[s] - GRACE_MS, 10_000); // at least 10s gap before warning
  } catch {
    return null;
  }
}

export interface AutoSignOutHookResult {
  showWarning: boolean;
  countdown: number;   // seconds remaining in grace period
  dismiss: () => void; // user clicked "Stay Signed In"
  signOutNow: () => void;
}

/**
 * Tracks user activity and triggers an auto sign-out after the configured
 * idle period.  A 2-minute warning modal is shown before sign-out fires.
 *
 * @param onSignOut – called when the session should be terminated.
 */
export function useAutoSignOut(onSignOut: () => void): AutoSignOutHookResult {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(GRACE_SECONDS);

  // Refs — avoids stale-closure issues in event callbacks
  const inGraceRef      = useRef(false);
  const idleRef         = useRef<ReturnType<typeof setTimeout> | null>(null);
  const graceRef        = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const onSignOutRef    = useRef(onSignOut);
  onSignOutRef.current  = onSignOut; // always current without triggering effects

  // ── Helpers ──────────────────────────────────────────────────────────────

  const stopAll = useCallback(() => {
    if (idleRef.current)     { clearTimeout(idleRef.current);     idleRef.current = null; }
    if (graceRef.current)    { clearTimeout(graceRef.current);    graceRef.current = null; }
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    inGraceRef.current = false;
  }, []);

  const executeSignOut = useCallback(() => {
    stopAll();
    setShowWarning(false);
    onSignOutRef.current();
  }, [stopAll]);

  const startGrace = useCallback(() => {
    inGraceRef.current = true;
    let secs = GRACE_SECONDS;
    setCountdown(secs);
    setShowWarning(true);

    // Tick every second to update the displayed countdown
    intervalRef.current = setInterval(() => {
      secs -= 1;
      setCountdown(secs);
      if (secs <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 1000);

    // Actual sign-out fires at the end of the grace period
    graceRef.current = setTimeout(executeSignOut, GRACE_MS);
  }, [executeSignOut]);

  const arm = useCallback(() => {
    if (idleRef.current) clearTimeout(idleRef.current);
    const ms = getIdleMs();
    if (ms === null) return; // timeout disabled or not set
    idleRef.current = setTimeout(startGrace, ms);
  }, [startGrace]);

  // ── Public API ────────────────────────────────────────────────────────────

  const dismiss = useCallback(() => {
    stopAll();
    setShowWarning(false);
    setCountdown(GRACE_SECONDS);
    arm();
  }, [stopAll, arm]);

  // ── Activity detection ────────────────────────────────────────────────────

  useEffect(() => {
    if (getIdleMs() === null) return; // nothing to do if disabled

    const onActivity = () => {
      if (inGraceRef.current) return; // don't reset during grace period
      arm();
    };

    const events = [
      'mousemove', 'mousedown', 'keydown',
      'touchstart', 'scroll', 'click',
    ] as const;

    events.forEach(e => document.addEventListener(e, onActivity, { passive: true }));
    arm(); // start the first idle timer

    return () => {
      events.forEach(e => document.removeEventListener(e, onActivity));
      stopAll();
    };
  }, [arm, stopAll]);

  // ── React to setting changes ───────────────────────────────────────────────

  const rearm = useCallback(() => {
    stopAll();
    setShowWarning(false);
    setCountdown(GRACE_SECONDS);
    arm();
  }, [stopAll, arm]);

  useEffect(() => {
    // Cross-tab changes via the Web Storage API
    const onStorage = (e: StorageEvent) => {
      if (e.key === AUTO_SIGNOUT_KEY) rearm();
    };
    // Same-tab changes dispatched by SettingsView after saving
    const onCustom = () => rearm();

    window.addEventListener('storage', onStorage);
    window.addEventListener('autoSignOutSettingChanged', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('autoSignOutSettingChanged', onCustom);
    };
  }, [rearm]);

  return { showWarning, countdown, dismiss, signOutNow: executeSignOut };
}
